import { useQuery } from "@tanstack/react-query";
import { api } from "@shared/routes";
import AdminLayout from "@/components/layout-admin";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard,
  Receipt,
  DollarSign,
  Calendar,
} from "lucide-react";

const billingModelLabels: Record<string, string> = {
  flat: "Flat Monthly Fee",
  per_apartment: "Per Apartment",
  per_usage: "Per Usage (checkout)",
  custom: "Custom Amount",
};

const cycleLabels: Record<string, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
};

const invoiceStatusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  draft: { label: "Draft", variant: "outline" },
  issued: { label: "Issued", variant: "secondary" },
  paid: { label: "Paid", variant: "default" },
  overdue: { label: "Overdue", variant: "destructive" },
  void: { label: "Void", variant: "outline" },
};

export default function AdminBilling() {
  const { data: plan, isLoading: planLoading } = useQuery<any>({
    queryKey: [api.adminBilling.plan.path],
  });

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery<any[]>({
    queryKey: [api.adminBilling.invoices.path],
  });

  const totalBilled = invoices.reduce((sum: number, inv: any) => sum + parseFloat(inv.total || '0'), 0);
  const totalPaid = invoices.filter((i: any) => i.status === 'paid').reduce((sum: number, inv: any) => sum + parseFloat(inv.total || '0'), 0);
  const totalOutstanding = invoices.filter((i: any) => ['issued', 'overdue'].includes(i.status)).reduce((sum: number, inv: any) => sum + parseFloat(inv.total || '0'), 0);

  const isLoading = planLoading || invoicesLoading;

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-display font-bold" data-testid="text-billing-heading">Billing</h1>
          <p className="text-sm text-muted-foreground mt-1">View your organization's billing plan and invoices.</p>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading billing information...</div>
        ) : (
          <>
            {/* Current Plan */}
            <Card data-testid="card-billing-plan">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold">Current Plan</h2>
                </div>
                {!plan ? (
                  <p className="text-muted-foreground">No billing plan has been configured for your organization yet. Contact the platform administrator for billing setup.</p>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="text-sm text-muted-foreground">Billing Model</p>
                      <p className="font-medium" data-testid="text-plan-model">{billingModelLabels[plan.model] || plan.model}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Billing Cycle</p>
                      <p className="font-medium" data-testid="text-plan-cycle">{cycleLabels[plan.billingCycle] || plan.billingCycle}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Amount</p>
                      <p className="font-medium text-lg" data-testid="text-plan-amount">
                        {plan.model === 'flat' && `$${parseFloat(plan.flatAmount || '0').toFixed(2)}`}
                        {plan.model === 'per_apartment' && `$${parseFloat(plan.perApartmentAmount || '0').toFixed(2)} / apartment`}
                        {plan.model === 'per_usage' && `$${parseFloat(plan.perUsageAmount || '0').toFixed(2)} / checkout`}
                        {plan.model === 'custom' && `$${parseFloat(plan.customAmount || '0').toFixed(2)}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <Badge variant={plan.isActive ? "default" : "outline"} className="no-default-active-elevate" data-testid="badge-plan-status">
                        {plan.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    {plan.notes && (
                      <div className="sm:col-span-2">
                        <p className="text-sm text-muted-foreground">Notes</p>
                        <p className="text-sm">{plan.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
              <Card className="border-none shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-50 dark:bg-blue-950">
                    <DollarSign className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Billed</p>
                    <p className="text-xl font-bold" data-testid="text-org-total-billed">${totalBilled.toFixed(2)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-full bg-green-50 dark:bg-green-950">
                    <DollarSign className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Paid</p>
                    <p className="text-xl font-bold text-green-600 dark:text-green-400" data-testid="text-org-total-paid">${totalPaid.toFixed(2)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-50 dark:bg-amber-950">
                    <DollarSign className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Outstanding</p>
                    <p className="text-xl font-bold text-amber-600 dark:text-amber-400" data-testid="text-org-outstanding">${totalOutstanding.toFixed(2)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Invoice History */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold">Invoice History</h2>
              </div>
              {invoices.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No invoices yet.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {invoices.map((inv: any) => {
                    const sc = invoiceStatusConfig[inv.status] || invoiceStatusConfig.draft;
                    return (
                      <Card key={inv.id} data-testid={`card-org-invoice-${inv.id}`}>
                        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Calendar className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">
                                {new Date(inv.periodStart).toLocaleDateString("en-US", { timeZone: "UTC" })} - {new Date(inv.periodEnd).toLocaleDateString("en-US", { timeZone: "UTC" })}
                              </span>
                              <Badge variant={sc.variant} className="no-default-active-elevate" data-testid={`badge-org-invoice-status-${inv.id}`}>
                                {sc.label}
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground space-y-0.5 pl-6">
                              <p>{billingModelLabels[inv.model] || inv.model}</p>
                              {inv.apartmentCount > 0 && <p>Apartments: {inv.apartmentCount}</p>}
                              {inv.usageCount > 0 && <p>Checkouts: {inv.usageCount}</p>}
                              {parseFloat(inv.adjustments || '0') !== 0 && (
                                <p>Adjustments: ${parseFloat(inv.adjustments || '0').toFixed(2)}</p>
                              )}
                              {inv.dueDate && <p>Due: {new Date(inv.dueDate).toLocaleDateString("en-US", { timeZone: "UTC" })}</p>}
                              {inv.notes && <p>Notes: {inv.notes}</p>}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold" data-testid={`text-org-invoice-total-${inv.id}`}>
                              ${parseFloat(inv.total || '0').toFixed(2)}
                            </p>
                            {inv.paidAt && (
                              <p className="text-xs text-muted-foreground">Paid: {new Date(inv.paidAt).toLocaleDateString("en-US", { timeZone: "UTC" })}</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
